import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, Loader2, FileText, IndianRupee, Printer,
  CheckCircle2, CreditCard, Wallet, Banknote, Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvoiceView = () => {
  const navigate = useNavigate();
  const { jobId, invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [job, setJob] = useState(null);
  const [garageProfile, setGarageProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentMode, setPaymentMode] = useState('cash');

  useEffect(() => {
    fetchInvoiceData();
  }, [invoiceId, jobId]);

  const fetchInvoiceData = async () => {
    const token = localStorage.getItem('garage_token');
    try {
      // Fetch invoice
      const invoiceResponse = await fetch(`${API_URL}/api/garage/invoices/${invoiceId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const invoiceData = await invoiceResponse.json();
      
      if (invoiceData.success) {
        setInvoice(invoiceData.invoice);
      }

      // Fetch job for additional details
      if (jobId) {
        const jobResponse = await fetch(`${API_URL}/api/garage/jobs/${jobId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const jobData = await jobResponse.json();
        if (jobData.success) {
          setJob(jobData.job);
        }
      }

      // Fetch garage profile
      const profileResponse = await fetch(`${API_URL}/api/garage/dashboard/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const profileData = await profileResponse.json();
      if (profileData.success) {
        setGarageProfile(profileData.profile);
      }
    } catch (error) {
      toast.error('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleRecordPayment = async () => {
    setProcessingPayment(true);
    const token = localStorage.getItem('garage_token');

    try {
      const response = await fetch(`${API_URL}/api/garage/invoices/${invoiceId}/payment`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ payment_mode: paymentMode })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Payment recorded successfully!');
        fetchInvoiceData();
      } else {
        toast.error(data.detail || 'Failed to record payment');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handlePrint = async () => {
    const token = localStorage.getItem('garage_token');
    try {
      const response = await fetch(`${API_URL}/api/garage/invoices/${invoiceId}/html`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const html = await response.text();
        const printWindow = window.open('', '_blank');
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.print();
      }
    } catch (error) {
      toast.error('Failed to generate printable invoice');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Card className="bg-slate-800/50 border-slate-700 p-8 text-center">
          <FileText className="w-16 h-16 mx-auto mb-4 text-slate-500" />
          <p className="text-white">Invoice not found</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 border-b border-slate-700 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(`/garage/jobs/${jobId}`)}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-green-400" />
                Invoice #{invoice.invoice_number}
              </h1>
              <p className="text-xs text-slate-400">
                {invoice.is_gst_invoice ? 'Tax Invoice' : 'Invoice'} • {invoice.customer_name}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handlePrint}
              variant="outline"
              className="border-slate-600 text-slate-300"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            {invoice.is_paid ? (
              <Badge className="bg-green-600 text-lg px-4 py-1">PAID</Badge>
            ) : (
              <Badge className="bg-yellow-500 text-lg px-4 py-1">UNPAID</Badge>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Invoice Header */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6">
            <div className="flex justify-between items-start mb-6">
              {/* Garage Info (Left) */}
              <div>
                {garageProfile?.logo_url ? (
                  <img 
                    src={`${API_URL}${garageProfile.logo_url}`} 
                    alt="Garage Logo" 
                    className="h-16 mb-2 rounded"
                  />
                ) : (
                  <h2 className="text-xl font-bold text-white">{garageProfile?.name}</h2>
                )}
                <p className="text-slate-400 text-sm">{garageProfile?.address}</p>
                <p className="text-slate-400 text-sm">{garageProfile?.mobile}</p>
                {invoice.garage_gst_number && (
                  <p className="text-slate-400 text-sm mt-1">GSTIN: {invoice.garage_gst_number}</p>
                )}
              </div>
              
              {/* Propitstop Logo (Right) */}
              <div className="text-right">
                <img 
                  src="/propitstop-logo.png" 
                  alt="Propitstop" 
                  className="h-10 ml-auto"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 border-t border-slate-700 pt-6">
              {/* Bill To */}
              <div>
                <h3 className="text-slate-400 text-sm mb-2">BILL TO</h3>
                <p className="text-white font-medium">{invoice.customer_name}</p>
                <p className="text-slate-400 text-sm">{invoice.customer_mobile}</p>
                {invoice.customer_email && (
                  <p className="text-slate-400 text-sm">{invoice.customer_email}</p>
                )}
              </div>

              {/* Vehicle & Invoice Details */}
              <div className="text-right">
                <h3 className="text-slate-400 text-sm mb-2">VEHICLE</h3>
                <p className="text-white">
                  {invoice.vehicle_make} {invoice.vehicle_model}
                </p>
                <p className="text-slate-400 text-sm">{invoice.vehicle_number}</p>
                <p className="text-slate-400 text-sm mt-2">
                  Date: {new Date(invoice.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Line Items */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="py-3 bg-slate-700/50">
            <div className="grid grid-cols-12 text-sm font-medium text-slate-300">
              <span className="col-span-1">#</span>
              <span className="col-span-5">Description</span>
              <span className="col-span-2 text-center">Type</span>
              <span className="col-span-1 text-center">Qty</span>
              <span className="col-span-1 text-right">Rate</span>
              <span className="col-span-2 text-right">Amount</span>
            </div>
          </CardHeader>
          <CardContent className="p-0 divide-y divide-slate-700">
            {invoice.line_items?.map((item, idx) => (
              <div key={idx} className="grid grid-cols-12 py-3 px-4 text-sm items-center">
                <span className="col-span-1 text-slate-400">{idx + 1}</span>
                <span className="col-span-5 text-white">
                  {item.part_name || item.description}
                </span>
                <span className="col-span-2 text-center">
                  <Badge variant="outline" className={`text-xs ${
                    item.item_type === 'part' ? 'border-green-600 text-green-400' : 
                    item.item_type === 'body_shop_labour' ? 'border-purple-600 text-purple-400' : 
                    'border-slate-600'
                  }`}>
                    {item.item_type === 'part' ? 'Part' : item.item_type === 'body_shop_labour' ? 'Body Shop' : 'Labour'}
                  </Badge>
                </span>
                <span className="col-span-1 text-center text-slate-300">{item.quantity}</span>
                <span className="col-span-1 text-right text-slate-300">
                  ₹{item.unit_price?.toLocaleString()}
                </span>
                <span className="col-span-2 text-right text-white font-medium">
                  ₹{item.total_amount?.toLocaleString()}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Totals */}
        <Card className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-500/30">
          <CardContent className="p-4">
            <div className="space-y-2 max-w-xs ml-auto">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Parts Subtotal</span>
                <span className="text-white">₹{invoice.subtotal_parts?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Labour Subtotal</span>
                <span className="text-white">₹{invoice.subtotal_labour?.toLocaleString()}</span>
              </div>
              {invoice.is_gst_invoice && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">CGST (9%)</span>
                    <span className="text-white">₹{invoice.cgst_amount?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">SGST (9%)</span>
                    <span className="text-white">₹{invoice.sgst_amount?.toLocaleString()}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between text-xl font-bold border-t border-orange-500/30 pt-3 mt-3">
                <span className="text-white">Grand Total</span>
                <span className="text-orange-400">₹{invoice.grand_total?.toLocaleString()}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Section */}
        {!invoice.is_paid && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="py-3">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-green-400" />
                Record Payment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-4 gap-2">
                {[
                  { value: 'cash', icon: Banknote, label: 'Cash' },
                  { value: 'upi', icon: Wallet, label: 'UPI' },
                  { value: 'card', icon: CreditCard, label: 'Card' },
                  { value: 'bank_transfer', icon: IndianRupee, label: 'Bank' }
                ].map(mode => (
                  <button
                    key={mode.value}
                    onClick={() => setPaymentMode(mode.value)}
                    className={`p-3 rounded-lg border ${
                      paymentMode === mode.value
                        ? 'border-orange-500 bg-orange-500/10 text-orange-400'
                        : 'border-slate-600 text-slate-400 hover:border-slate-500'
                    } flex flex-col items-center gap-1 transition-colors`}
                  >
                    <mode.icon className="w-5 h-5" />
                    <span className="text-xs">{mode.label}</span>
                  </button>
                ))}
              </div>
              
              <Button
                onClick={handleRecordPayment}
                disabled={processingPayment}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                data-testid="record-payment-btn"
              >
                {processingPayment ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Mark as Paid (₹{invoice.grand_total?.toLocaleString()})
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Payment Confirmation */}
        {invoice.is_paid && (
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="p-4 flex items-center gap-4">
              <CheckCircle2 className="w-12 h-12 text-green-500" />
              <div>
                <p className="text-green-400 font-medium text-lg">Payment Received</p>
                <p className="text-slate-400 text-sm">
                  Paid via {invoice.payment_mode?.replace('_', ' ').toUpperCase()} on{' '}
                  {invoice.paid_at ? new Date(invoice.paid_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center py-4 text-slate-500 text-xs">
          Powered by Propitstop
        </div>
      </main>
    </div>
  );
};

export default InvoiceView;
